import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import { Bell, Loader2, Calendar, Lock } from 'lucide-react'

const FEED_LIMIT = 10

const TYPE_ICON = {
  event: Calendar,
  group: Lock,
}

/**
 * Header notification bell — recent announcements across every parish/group
 * the current user belongs to. Opening the dropdown marks the loaded page
 * read (per-item, not a single "seen" cursor); "View more" loads and marks
 * read the next page too, so every unread item is eventually reachable —
 * unreadCount is computed server-side over the whole feed, not just what's
 * currently loaded, so anything the bell never lets you page to would
 * otherwise stay unread forever.
 */
export default function AnnouncementBell() {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [items, setItems] = useState([])
  const [page, setPage] = useState(1)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const menuRef = useRef(null)

  const fetchFeed = async () => {
    try {
      setLoading(true)
      const response = await api.getMyAnnouncementsFeed({ page: 1, limit: FEED_LIMIT })
      setItems(response.data || [])
      setUnreadCount(response.unreadCount || 0)
      setHasNextPage(Boolean(response.meta?.hasNextPage))
      setPage(1)
    } catch (err) {
      console.error('Failed to fetch announcements feed:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchFeed()
  }, [])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const markAsRead = async (toMark) => {
    if (toMark.length === 0) return
    const keys = new Set(toMark.map((item) => `${item.type}:${item.id}`))
    setItems((prev) =>
      prev.map((item) => (keys.has(`${item.type}:${item.id}`) ? { ...item, isUnread: false } : item)),
    )
    setUnreadCount((prev) => Math.max(0, prev - toMark.length))
    try {
      await api.markAnnouncementsRead(toMark.map((item) => ({ type: item.type, id: item.id })))
    } catch (err) {
      console.error('Failed to mark announcements read:', err)
    }
  }

  const handleToggle = () => {
    const opening = !isOpen
    setIsOpen(opening)
    if (!opening) return
    markAsRead(items.filter((item) => item.isUnread))
  }

  const handleViewMore = async () => {
    const nextPage = page + 1
    try {
      setLoadingMore(true)
      const response = await api.getMyAnnouncementsFeed({ page: nextPage, limit: FEED_LIMIT })
      const newItems = response.data || []
      setItems((prev) => [...prev, ...newItems])
      setHasNextPage(Boolean(response.meta?.hasNextPage))
      setPage(nextPage)
      await markAsRead(newItems.filter((item) => item.isUnread))
    } catch (err) {
      console.error('Failed to fetch more announcements:', err)
    } finally {
      setLoadingMore(false)
    }
  }

  const handleItemClick = (item) => {
    setIsOpen(false)
    navigate(`/app/donor/parish/${item.parishId}`)
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={handleToggle}
        aria-label="Announcements"
        className="relative p-2 rounded text-charcoal-muted hover:text-charcoal hover:bg-surface-border/40 transition-colors duration-150"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-gold text-[10px] font-semibold text-black">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded bg-white border border-surface-border shadow-subtle z-50">
          <div className="px-4 py-2 border-b border-surface-border">
            <p className="text-sm font-semibold text-charcoal">Announcements</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 text-gold animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <p className="px-4 py-6 text-sm text-charcoal-muted text-center">No announcements yet.</p>
          ) : (
            <div className="py-1">
              {items.map((item) => {
                const SourceIcon = TYPE_ICON[item.type]
                return (
                  <button
                    key={`${item.type}-${item.id}`}
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className="w-full text-left px-4 py-3 border-b border-surface-border last:border-b-0 hover:bg-surface-linen transition-colors duration-150"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-sm font-semibold text-charcoal">{item.title}</span>
                      {item.isUnread && <span className="w-2 h-2 mt-1 rounded-full bg-gold flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-charcoal-muted mt-1 line-clamp-2">{item.content}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-charcoal-subtle">
                      <span>{item.parishName}</span>
                      {SourceIcon && item.sourceName && (
                        <span className="inline-flex items-center gap-1">
                          <SourceIcon className="w-3 h-3" />
                          {item.sourceName}
                        </span>
                      )}
                    </div>
                  </button>
                )
              })}
              {hasNextPage && (
                <button
                  type="button"
                  onClick={handleViewMore}
                  disabled={loadingMore}
                  className="w-full text-center px-4 py-2 text-sm text-gold hover:text-gold/80 font-medium"
                >
                  {loadingMore ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'View more'}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
